import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ACCESS_OPTIONS = ['View only', 'Download', 'Full access'];
const ROLE_OPTIONS = ['Investor', 'Sponsor', 'Admin'];

export default function AccessPermissionsModal({ doc, userRole, onClose, onToast }) {
  const [accessList, setAccessList] = useState(doc?.accessList || []);
  const [newUser, setNewUser] = useState('');
  const [newRole, setNewRole] = useState('Investor');
  const [newAccess, setNewAccess] = useState('View only');

  const isAdmin = userRole === 'admin';

  const handleAddUser = () => {
    if (!newUser?.trim()) return;
    setAccessList((prev) => [
      ...prev,
      { name: newUser?.trim(), role: newRole, access: newAccess, granted: new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) },
    ]);
    setNewUser('');
    setNewRole('Investor');
    setNewAccess('View only');
  };

  const handleRemove = (idx) => {
    setAccessList((prev) => prev?.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    onToast({ type: 'success', title: 'Access updated', message: 'Permissions saved successfully.' });
    onClose();
  };

  const ACCESS_BADGE = {
    'View only': { color: '#2563eb', bg: 'rgba(37,99,235,0.1)', border: 'rgba(37,99,235,0.3)' },
    'Download': { color: '#d97706', bg: 'rgba(217,119,6,0.1)', border: 'rgba(217,119,6,0.3)' },
    'Full access': { color: '#16a34a', bg: 'rgba(22,163,74,0.1)', border: 'rgba(22,163,74,0.3)' },
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 'var(--z-modal, 1000)', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
    >
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10">
            <Icon name="Users" size={18} color="var(--color-primary)" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-base font-semibold text-foreground">Manage Access</h2>
            <p className="text-xs font-caption text-muted-foreground truncate">{doc?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Role-based note */}
          {!isAdmin && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <Icon name="AlertTriangle" size={15} color="#d97706" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs font-caption text-amber-700">
                {userRole === 'investor' ? 'Investor' : 'Sponsor'} role cannot modify access permissions.
              </p>
            </div>
          )}

          {/* Current Access List */}
          <div>
            <h3 className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Current Access</h3>
            <div className="space-y-2">
              {accessList?.map((entry, idx) => {
                const badge = ACCESS_BADGE?.[entry?.access] || ACCESS_BADGE?.['View only'];
                return (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted flex-shrink-0">
                      <Icon name="User" size={14} color="var(--color-muted-foreground)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-caption font-semibold text-foreground truncate">{entry?.name}</p>
                      <p className="text-xs font-caption text-muted-foreground">{entry?.role} · Granted {entry?.granted}</p>
                    </div>
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full font-caption border flex-shrink-0"
                      style={{ color: badge?.color, background: badge?.bg, borderColor: badge?.border }}
                    >
                      {entry?.access}
                    </span>
                    {isAdmin && (
                      <button
                        onClick={() => handleRemove(idx)}
                        className="flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-error hover:bg-error/10 transition-all duration-200 flex-shrink-0"
                        title="Remove access"
                      >
                        <Icon name="Trash2" size={13} />
                      </button>
                    )}
                  </div>
                );
              })}
              {accessList?.length === 0 && (
                <p className="text-sm font-caption text-muted-foreground text-center py-4">No users have access.</p>
              )}
            </div>
          </div>

          {/* Add User */}
          {isAdmin && (
            <div>
              <h3 className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Add User</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="User name or email"
                  value={newUser}
                  onChange={(e) => setNewUser(e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm font-caption text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-200"
                />
                <div className="flex gap-2">
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e?.target?.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-caption text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-200"
                  >
                    {ROLE_OPTIONS?.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <select
                    value={newAccess}
                    onChange={(e) => setNewAccess(e?.target?.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-caption text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-200"
                  >
                    {ACCESS_OPTIONS?.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <button
                  onClick={handleAddUser}
                  disabled={!newUser?.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-caption font-medium border border-primary text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <Icon name="UserPlus" size={14} /> Add User
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-caption font-medium text-muted-foreground hover:text-foreground hover:bg-muted border border-border transition-all duration-200"
          >
            Cancel
          </button>
          {isAdmin && (
            <button
              onClick={handleSave}
              className="px-5 py-2 rounded-lg text-sm font-caption font-semibold bg-primary text-white hover:bg-primary/90 transition-all duration-200"
            >
              Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
